import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type CohortAnalysisResult } from '@whinerp/client';
import { formatCount, formatMoney, formatPercent } from '@whinerp/contracts/format';
import { ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-71 Cohorts (F-101): cohort rows, period columns. Every cell carries its
 * number; the shading only repeats it, so the grid reads the same to somebody
 * who cannot tell the colours apart.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

function CohortsScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const [basis, setBasis] = useState<'first-purchase' | 'created'>('first-purchase');
  const [grain, setGrain] = useState<'month' | 'quarter'>('month');
  const [measure, setMeasure] = useState<'retention' | 'expansion'>('retention');
  const data = useQuery<CohortAnalysisResult, ApiError>({
    queryKey: ['cohorts', basis, grain],
    queryFn: () => api.cohortAnalysis({ query: { basis, grain, periods: 12 } }),
    placeholderData: (previous) => previous,
  });
  if (data.isPending) return <Skeleton className="h-plot w-full" />;
  if (data.isError) return <ErrorState title={t('state.error')} body={errorText(data.error)} onRetry={() => void data.refetch()} />;
  const result = data.data;
  return (
    <div data-component="Cohorts" className="flex w-full flex-col gap-4">
      <PageHeading>{t('cohorts.title')}</PageHeading>
      <div className="flex flex-wrap gap-2">
        <select data-cohort-basis aria-label={t('cohorts.basis')} className={INPUT} value={basis} onChange={(e) => setBasis(e.target.value as typeof basis)}>
          <option value="first-purchase">{t('cohorts.basis.first-purchase')}</option>
          <option value="created">{t('cohorts.basis.created')}</option>
        </select>
        <select data-cohort-grain aria-label={t('cohorts.grain')} className={INPUT} value={grain} onChange={(e) => setGrain(e.target.value as typeof grain)}>
          <option value="month">{t('cohorts.grain.month')}</option>
          <option value="quarter">{t('cohorts.grain.quarter')}</option>
        </select>
        <select data-cohort-measure aria-label={t('cohorts.measure')} className={INPUT} value={measure} onChange={(e) => setMeasure(e.target.value as typeof measure)}>
          <option value="retention">{t('cohorts.measure.retention')}</option>
          <option value="expansion">{t('cohorts.measure.expansion')}</option>
        </select>
      </div>
      <Panel>
        {result.rows.length === 0 ? (
          <p data-cohorts-empty className="text-body text-ink-meta">{t('cohorts.none')}</p>
        ) : (
          /* Marked as well as styled: `overflow-x-auto` is what makes it
             scroll, and `data-scroll-container` is what lets the render check
             see that it does. A cohort grid is one column per period and
             outgrows 1280px on any real workspace. */
          <div data-scroll-container className="overflow-x-auto">
            <table data-cohort-grid className="w-full text-dense">
              <thead>
                <tr>
                  <th className="p-1 text-start font-normal text-ink-meta">{t('cohorts.cohort')}</th>
                  <th className="p-1 text-end font-normal text-ink-meta">{t('cohorts.size')}</th>
                  {Array.from({ length: result.periods }, (_, k) => (
                    <th key={k} className="p-1 text-end font-normal text-ink-meta">{String(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row) => (
                  <tr key={row.cohort} data-cohort={row.cohort}>
                    <td className="p-1 text-ink">{row.cohort}</td>
                    <td className="p-1 text-end text-ink">{formatCount(row.size, format)}</td>
                    {row.retained.map((retained, k) => {
                      const share = measure === 'retention' ? (row.retentionShare[k] ?? 0) : Math.min(row.expansion[k] ?? 0, 2) / 2;
                      const label =
                        measure === 'retention'
                          ? `${formatPercent(row.retentionShare[k] ?? 0, format)} (${formatCount(retained, format)})`
                          : row.expansion[k] === null || row.expansion[k] === undefined
                            ? '-'
                            : formatMoney(row.revenue[k] ?? '0', format, { currency: result.currency });
                      return (
                        <td key={k} data-cell={`${row.cohort}:${String(k)}`} className="relative p-1 text-end text-ink">
                          <span aria-hidden className="absolute inset-0 bg-bronze" style={{ opacity: share * 0.5 }} />
                          <span className="relative">{label}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="pt-2 text-meta text-ink-meta">
          {t('cohorts.note', { values: { currency: result.currency } })}
          {result.otherCurrencyDeals > 0 ? ` ${t('cohorts.otherCurrency', { values: { count: formatCount(result.otherCurrencyDeals, format) } })}` : ''}
        </p>
      </Panel>
    </div>
  );
}

function Loading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function Failed(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('cohorts.none')} />;
}

export const screen = defineScreen({
  id: 'SC-71',
  route: '/analytics/cohorts',
  surface: 'page',
  title: 'Cohorts',
  features: ['F-101'],
  entitlement: 'F-101',
  reachedBy: [{ kind: 'screen', from: 'SC-53', how: 'Cohorts link on the reports list' }],
  components: [],
  elements: ['Cohorts'],
  states: { empty: Loading, loading: Loading, error: Failed },
  component: CohortsScreen,
});

export default screen;
